import React from 'react';
import { Download } from '../Download';
import { StatusMessages } from '../../Form2';
import { StatusInfoModifier } from '../StatusInfo/partials/StatusInfo';
import { ButtonAction } from '../../../components/molecules/Btn/ButtonWrapper';
import { FetchState } from '../../../hooks/reducers/useFetchReducer';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { useScrollIntoViewOnMutation } from './useScrollIntoViewOnMutation';

type ExportDownloadControlsProps = {
  buttonLabel: string;
  generatingMessage?: string;
  onGenerate: () => void;
  fetchState: FetchState<{ data: Blob }, string>;
  objectUrl?: string;
  fileName?: string;
  download?: string;
  fileExtension: string;
};

/**
 * Presentational wrapper around the common export/download flow: a button that
 * triggers generation, a loading indicator while the export is being created,
 * an error message, and a download link once the export is ready.
 */
export function ExportDownloadControls({
  buttonLabel,
  generatingMessage,
  onGenerate,
  fetchState,
  objectUrl,
  fileName,
  download,
  fileExtension,
}: ExportDownloadControlsProps) {
  const { t } = useSonTranslation('ExportDownloadControls');
  const containerRef = useScrollIntoViewOnMutation<HTMLDivElement>();

  const fileSizeBytes = fetchState.type === 'success' ? fetchState.response.data.size : undefined;

  return (
    <div className="export-download-controls" ref={containerRef}>
      <ButtonAction transparent text={buttonLabel} type="button" onClick={onGenerate} />
      <StatusMessages
        fetchState={fetchState}
        messageSuccess={t('success')}
        messageSending={generatingMessage ? generatingMessage : t('sending')}
        messageFail={t('fail')}
        successModifier={StatusInfoModifier.INVISIBLE}
        sendingModifier={[StatusInfoModifier.PADDING_TOP]}
        errorModifier={[StatusInfoModifier.PADDING_TOP, StatusInfoModifier.RED]}
      />
      {fetchState.type === 'success' && objectUrl && fileName && fileSizeBytes !== undefined && (
        <div className="export-download-controls__download">
          <Download
            url={objectUrl}
            fileName={fileName}
            download={download}
            fileExtension={fileExtension}
            fileSizeBytes={fileSizeBytes}
          />
        </div>
      )}
    </div>
  );
}
